import { useState, useEffect, useMemo, useRef } from "react";
import { observer } from "mobx-react-lite";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate, useParams } from "react-router-dom";
import Confetti from "react-confetti";

function useMediaQuery(query: string) {
  const [matches, setMatches] = useState(() => {
    if (typeof window === "undefined") return false;
    return window.matchMedia(query).matches;
  });
  useEffect(() => {
    const mql = window.matchMedia(query);
    const listener = (e: MediaQueryListEvent) => setMatches(e.matches);
    if (mql.addEventListener) mql.addEventListener("change", listener);
    else (mql as any).addListener(listener);
    return () => {
      if (mql.removeEventListener) mql.removeEventListener("change", listener);
      else (mql as any).removeListener(listener);
    };
  }, [query]);
  return matches;
}
import {
  ArrowRight,
  ArrowDownRight,
  Delete,
  MessageCircle,
  X,
  Copy,
  CheckCircle2,
  Clock,
  Search,
  LogIn,
  Menu,
  Moon,
  Sun,
  Info,
  MapPin,
  ChevronDown,
  Gift,
  Gamepad2,
  Users,
  Grid3x3,
  Check,
  Smartphone,
  Hand,
  Shield,
  User,
  Phone,
  Key,
  Wallet,
  Loader2,
  AlertCircle,
  Sparkles,
  TrendingUp,
} from "lucide-react";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { BottomSheet, BottomSheetContent, BottomSheetTitle, BottomSheetDescription } from "@/components/ui/bottom-sheet";
import { BottomNav } from "@/components/BottomNav";
import { isMobileApp } from "@/utils/browser";
import { copyToClipboard } from "@/lib/utils";
import { useCoreController } from "@/services/coreController";
import { analyticsService } from "@/services/analytics";
import { alertService } from "@/services/alertService";
import { useAuth } from "@/hooks/useAuth";
import { useTheme } from "@/hooks/useTheme";
import { Compte } from "@/types/app";
import { Notification } from "@/components/Notification";
import { appApi } from "@/services/api";
import { directusService } from "@/services/directus";
import { usePaymentTracking } from "@/hooks/usePaymentTracking";
import { storage } from "@/lib/storage";
import { PLATFORM_ACCENT } from "@/lib/app-data";
import {
  OperatorConfig,
  getOperatorFromPrefix,
  AppConstants as ThemeAppConstants,
  calculateBonus,
  calculateTotalWithBonus,
} from "@/constants/theme";
import { getCommercialIdFromUrl } from "@/utils/partner";
import easyserviceLogo from "@/assets/easyservice.png";
import { notifyNewUserOnboarding } from "@/components/NewUserOnboardingModal";
import { COMPLETED_KEY, DISMISSED_KEY } from "@/hooks/useNewUserOnboarding";

const AppConstants = {
  minAmount: 250,
  minWithdrawAmount: 900,
  maxAmount: 10000000,
};

type TxType = "deposit" | "withdraw";
type Operator = "Orange" | "Moov";
type QuickActionId = "play" | "refer" | "more";
type PaymentFlow = "form" | "payment" | "success" | "error";
type WithdrawFlow = "form" | "success" | "error";
type OnboardingStep = "welcome" | "success";

const QUICK = [500, 1000, 5000, 10000];

const PLATFORM_URL: Record<string, string> = {
  "1XBET": "https://1xbet.com",
  MELBET: "https://melbet.com",
  BETWINNER: "https://betwinner.com",
};

const PLATFORM_THEME: Record<string, { from: string; via: string; to: string; chip: string }> = {
  "1XBET": { from: "var(--page-gradient-from)", via: "var(--page-gradient-via)", to: "var(--page-gradient-to)", chip: "var(--accent-brand)" },
  MELBET: { from: "var(--page-gradient-from)", via: "var(--page-gradient-via)", to: "var(--page-gradient-to)", chip: "#FF9800" },
  BETWINNER: { from: "var(--page-gradient-from)", via: "var(--page-gradient-via)", to: "var(--page-gradient-to)", chip: "#0052cc" },
};

const DEFAULT_THEME = { from: "var(--page-gradient-from)", via: "var(--page-gradient-via)", to: "var(--page-gradient-to)", chip: "var(--accent-brand)" };

const PLATFORM_LABEL_SPLIT: Record<string, { prefix: string; suffix: string }> = {
  "1XBET": { prefix: "1X", suffix: "BET" },
  MELBET: { prefix: "MEL", suffix: "BET" },
  BETWINNER: { prefix: "BET", suffix: "WINNER" },
};

const TIPS: Record<"deposit" | "withdraw", string[]> = {
  deposit: [
    "Ton ID compte se trouve en haut de l'app du bookmaker.",
    "Crédit en moins de 30s après ton paiement Mobile Money.",
    "Aucun mot de passe demandé — ne le partage jamais.",
  ],
  withdraw: [
    "Point de retrait : Ouaga · Rue Express Multi Service.",
    "Génère ton code retrait sur le bookmaker puis colle-le ici.",
  ],
};

const TOUR_STEPS = [
  { title: "1 · Choisis ta plateforme", desc: "1XBET, MELBET ou BETWINNER. Le thème s'adapte automatiquement." },
  { title: "2 · Saisis le montant", desc: "Utilise le clavier ou les raccourcis. Minimum 100 FCFA." },
  { title: "3 · Continuer", desc: "Renseigne ton ID compte puis valide. Crédit en moins de 30s." },
];

const formatAmount = (a: string | number) => {
  const num = typeof a === "string" ? Number(a) : a;
  if (!num) return "0";
  return num.toLocaleString("fr-FR");
};

const formatTime = (seconds: number) => {
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return `${mins}:${secs.toString().padStart(2, "0")}`;
};

const HomePage = observer(() => {
  const navigate = useNavigate();
  const coreController = useCoreController();
  const { signOutUser, user: newUser } = useAuth();
  const { theme: uiTheme, toggleTheme } = useTheme();
  const { commercialId } = useParams();

  // ---- Core UI state ----
  const [amount, setAmount] = useState<string>("250");
  const [selectedAccount, setSelectedAccount] = useState<Compte | null>(null);
  const [isLoadingBookmakers, setIsLoadingBookmakers] = useState<boolean>(true);
  const [type, setType] = useState<TxType>("deposit");
  const [showForm, setShowForm] = useState(false);
  const [tipIdx, setTipIdx] = useState(0);
  const [quickAction, setQuickAction] = useState<QuickActionId | null>(null);
  const [menuOpen, setMenuOpen] = useState(false);
  // Position draggable du bouton WhatsApp (en pixels depuis le bord droit/bas)
  const [waPos, setWaPos] = useState<{ x: number; y: number }>(() => {
    if (typeof window === "undefined") return { x: 20, y: 80 };
    try {
      const saved = window.localStorage.getItem("es.wa.pos");
      if (saved) return JSON.parse(saved);
    } catch {}
    return { x: 20, y: 80 };
  });
  const waDragRef = useRef<{ startX: number; startY: number; origX: number; origY: number; moved: boolean } | null>(null);
  const waJustMovedRef = useRef(false);
  const [showReferralBanner, setShowReferralBanner] = useState<boolean>(true);
  const [notification, setNotification] = useState<{ isVisible: boolean; message: string; type: "error" | "success" }>({
    isVisible: false,
    message: "",
    type: "error",
  });
  const [minAmountAlert, setMinAmountAlert] = useState<{ open: boolean; flowType: TxType | null }>({
    open: false,
    flowType: null,
  });

  // ---- Deposit state ----
  const [accountId, setAccountId] = useState("");
  const [userNameStatus, setUserNameStatus] = useState<"idle" | "loading" | "found" | "notfound">("idle");
  const [resolvedName, setResolvedName] = useState("");
  const [operator, setOperator] = useState<Operator>("Orange");
  const [paymentPhone, setPaymentPhone] = useState("");
  const [paymentFlow, setPaymentFlow] = useState<PaymentFlow>("form");
  const [withdrawFlow, setWithdrawFlow] = useState<WithdrawFlow>("form");
  const [paymentIntentId, setPaymentIntentId] = useState<string | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [remainingSeconds, setRemainingSeconds] = useState<number>(ThemeAppConstants.sessionDuration);
  const [showConfetti, setShowConfetti] = useState(false);
  const timerRef = useRef<number | null>(null);

  // ---- Withdraw state ----
  const [withdrawPhone, setWithdrawPhone] = useState("");
  const [withdrawName, setWithdrawName] = useState("");
  const [withdrawCode, setWithdrawCode] = useState("");
  const [showHowto, setShowHowto] = useState(false);
  const [isWithdrawLoading, setIsWithdrawLoading] = useState(false);

  // ---- Onboarding / tour ----
  const [showOnboard, setShowOnboard] = useState(false);
  const [onboardingSubmitting, setOnboardingSubmitting] = useState(false);
  const onboardingOpenedRef = useRef(false);
  const [onboardingStep, setOnboardingStep] = useState<OnboardingStep>("welcome");
  const [onboardingPhone, setOnboardingPhone] = useState("");
  const [onboardingName, setOnboardingName] = useState("");
  const [startTourAfterOnboard, setStartTourAfterOnboard] = useState(false);
  const [tourStep, setTourStep] = useState<number>(-1);

  const accounts = coreController.appSettingResponse.compte || [];
  const theme = selectedAccount?.type ? PLATFORM_THEME[selectedAccount.type] || DEFAULT_THEME : DEFAULT_THEME;
  const showTip = useMediaQuery("(min-width: 340px)");

  // ---- WebSocket payment tracking ----
  const handlePaymentSuccess = () => {
    setPaymentFlow("success");
    setShowConfetti(true);
    analyticsService.trackEvent("payment_success", {
      amount: Number(amount),
      paymentMethod: "mobile_money",
      transactionType: "deposit",
      transactionId: paymentIntentId || undefined,
    });
    showNotification("Opération terminée avec succès !", "success");
    cleanupTimer();
    // Ne pas fermer automatiquement — l'utilisateur dismiss lui-même
  };

  const handlePaymentError = (error: Error) => {
    setPaymentFlow("error");
    analyticsService.trackEvent("payment_failure", {
      amount: Number(amount),
      reason: error.message,
      transactionType: "deposit",
    });
    showNotification("Paiement en attente de confirmation. Vérifiez votre solde avant de réessayer.");
    cleanupTimer();
  };

  const { trackingStatus, isPollingMode } = usePaymentTracking({
    paymentIntentId: paymentIntentId || "",
    apiKey: coreController.appSettingResponse.apiKey || "",
    onSuccess: handlePaymentSuccess,
    onError: handlePaymentError,
  });

  // ---- Effects ----
  useEffect(() => {
    if (commercialId) {
      coreController.setCommercialId(commercialId);
      analyticsService.setCommercialId(commercialId);
    }
  }, [commercialId, coreController]);

  useEffect(() => {
    analyticsService.trackScreenView("home_page");
    const hasClosedBanner = localStorage.getItem("referralBannerClosed");

    const hasCompleted = localStorage.getItem(COMPLETED_KEY) === 'true'
    const hasDismissed = localStorage.getItem(DISMISSED_KEY) === 'true'
    const hasSettedId = storage.read(`user_id_1XBET`) !== null || storage.read(`user_id_BETWINNER`) !== null || storage.read(`user_id_MELBET`) !== null

    if (hasClosedBanner === "true") setShowReferralBanner(false);

    // Onboarding first visit — guard against double open (Strict Mode / fast re-render)
    const onboarded = localStorage.getItem("es.onboarded");
    if (!onboarded && !hasCompleted && !hasDismissed && !hasSettedId && !onboardingOpenedRef.current) {
      onboardingOpenedRef.current = true;
      setShowOnboard(true);
    }
  }, []);



  useEffect(() => {
    if (accounts.length > 0 && !selectedAccount) {
      const sorted = [...accounts].sort((a) => (a.type === "1XBET" ? -1 : 1));
      const first = sorted[0];
      setSelectedAccount(first);
      coreController.setAccount(first);
    }
    if (accounts.length > 0) {
      setIsLoadingBookmakers(false);
    } else {
      const timeout = setTimeout(() => setIsLoadingBookmakers(false), 10000);
      return () => clearTimeout(timeout);
    }
  }, [accounts, selectedAccount, coreController]);

  useEffect(() => {
    const id = setInterval(() => setTipIdx((i) => (i + 1) % TIPS[type].length), 4200);
    return () => clearInterval(id);
  }, [type]);
  useEffect(() => setTipIdx(0), [type]);

  // Load saved values when account changes
  useEffect(() => {
    if (!selectedAccount) return;
    const savedId = storage.read<string>(`user_id_${selectedAccount.type}`) || "";
    setAccountId(savedId);
    setPaymentPhone(storage.read<string>("pay_with") || "");
    setWithdrawPhone(storage.read<string>("withdraw_phone") || "");
    setWithdrawName(storage.read<string>("withdraw_name") || "");
    setResolvedName("");
    setUserNameStatus("idle");
  }, [selectedAccount]);

  // Auto-save values
  useEffect(() => {
    if (selectedAccount && accountId) storage.write(`user_id_${selectedAccount.type}`, accountId);
  }, [accountId, selectedAccount]);

  useEffect(() => {
    if (paymentPhone) storage.write("pay_with", paymentPhone);
  }, [paymentPhone]);

  useEffect(() => {
    if (withdrawPhone) storage.write("withdraw_phone", withdrawPhone);
  }, [withdrawPhone]);

  useEffect(() => {
    if (withdrawName) storage.write("withdraw_name", withdrawName);
  }, [withdrawName]);

  // Detect operator from payment phone
  useEffect(() => {
    const clean = paymentPhone.replace(/\D/g, "");
    if (clean.length >= 2) {
      const op = getOperatorFromPrefix(clean.substring(0, 2));
      setOperator(op === "orange" ? "Orange" : op === "moov" ? "Moov" : "Orange");
    }
  }, [paymentPhone]);

  // Verify user name (debounced)
  useEffect(() => {
    if (!selectedAccount || !accountId.trim() || accountId.length < 3) {
      setUserNameStatus("idle");
      setResolvedName("");
      return;
    }
    if (!/^\d+$/.test(accountId)) {
      setUserNameStatus("notfound");
      setResolvedName("");
      return;
    }
    setUserNameStatus("loading");
    setResolvedName("");
    const timer = setTimeout(async () => {
      try {
        const name = await appApi.getUserName(accountId.trim(), selectedAccount.id);
        if (name === null || name === "undefined undefined undefined") {
          setUserNameStatus("notfound");
        } else {
          setResolvedName(name);
          setUserNameStatus("found");
        }
      } catch {
        setUserNameStatus("notfound");
      }
    }, 600);
    return () => clearTimeout(timer);
  }, [accountId, selectedAccount]);

  // Session timer for payment
  const startSessionTimer = () => {
    cleanupTimer();
    setRemainingSeconds(ThemeAppConstants.sessionDuration);
    timerRef.current = window.setInterval(() => {
      setRemainingSeconds((prev) => {
        if (prev <= 1) {
          cleanupTimer();
          handleTimeout();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  const cleanupTimer = () => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
  };

  const handleTimeout = () => {
    showNotification("Votre session de paiement a expiré");
    setPaymentFlow("error");
  };

  const resetPaymentFlow = () => {
    cleanupTimer();
    setPaymentFlow("form");
    setPaymentIntentId(null);
    setRemainingSeconds(ThemeAppConstants.sessionDuration);
    hasAutoDialedRef.current = false;
  };

  useEffect(() => {
    return () => cleanupTimer();
  }, []);

  // ---- Helpers ----
  const showNotification = (message: string, type: "error" | "success" = "error") => {
    setNotification({ isVisible: true, message, type });
  };

  const hideNotification = () => {
    setNotification((prev) => ({ ...prev, isVisible: false }));
  };

  const handleAccountSelect = (account: Compte) => {
    setSelectedAccount(account);
    coreController.setAccount(account);
  };

  const handleNumberClick = (num: string) => {
    if (amount === "0" || amount === "") {
      setAmount(num);
    } else {
      const newAmount = amount + num;
      if (parseInt(newAmount) <= AppConstants.maxAmount) setAmount(newAmount);
    }
  };

  const handleDelete = () => {
    if (amount.length > 1) setAmount(amount.slice(0, -1));
    else setAmount("0");
  };

  const pressKey = (k: string) => {
    if (k === "del") return handleDelete();
    if (k === "." && amount.includes(".")) return;
    handleNumberClick(k);
  };

  const amountOk = useMemo(() => {
    if (type === "deposit") return Number(amount) >= AppConstants.minAmount;
    return Number(amount) >= AppConstants.minWithdrawAmount;
  }, [amount, type]);

  const canSubmitDeposit = useMemo(() => {
    const phoneOk = paymentPhone.replace(/\D/g, "").length === 8;
    return amountOk && userNameStatus === "found" && phoneOk && accountId.trim().length >= 3;
  }, [amountOk, userNameStatus, paymentPhone, accountId]);

  const canSubmitWithdraw = useMemo(() => {
    const phone = withdrawPhone.replace(/\D/g, "");
    const phoneOk = phone.length === 8 && /^\d+$/.test(phone) && !/^(01|02|03|50|51|52|53)/.test(phone);
    return (
      amountOk &&
      phoneOk &&
      withdrawName.trim().length >= 2 &&
      !/\d/.test(withdrawName) &&
      withdrawCode.trim().length >= 4 &&
      accountId.trim().length >= 3 &&
      /^\d+$/.test(accountId)
    );
  }, [amountOk, withdrawPhone, withdrawName, withdrawCode, accountId]);

  // ---- Deposit flow ----
  const handlePrimaryAction = () => {
    if (!amountOk || !selectedAccount) return;
    setShowForm(true);
  };

  const startFlow = (flowType: TxType) => {
    if (!selectedAccount) return;
    const min = flowType === "deposit" ? AppConstants.minAmount : AppConstants.minWithdrawAmount;
    if (Number(amount) < min) {
      setMinAmountAlert({ open: true, flowType });
      return;
    }
    setType(flowType);
    setShowForm(true);
  };

  const handleStartDeposit = async () => {
    if (!selectedAccount || !canSubmitDeposit) return;
    setIsProcessing(true);

    try {
      const balanceOk = await coreController.checkBalanceForDeposit(Number(amount), selectedAccount.id);
      if (!balanceOk) {
        alertService.sendInfoAlert({
          title: "Dépôt indisponible",
          message: `Dépôt momentanément indisponible pour le compte ${selectedAccount.type} (ID: ${selectedAccount.id}) en raison de fonds insuffisants. Montant demandé: ${amount} FCFA.`,
        });
        showNotification("Dépôt momentanément indisponible. Veuillez réessayer plus tard.", "error");
        setIsProcessing(false);
        return;
      }

      await analyticsService.trackDepositInitiated({
        amount: Number(amount),
        accountType: selectedAccount.type,
        commercialId: coreController.commercialId || undefined,
      });

      setPaymentFlow("payment");
      startSessionTimer();

      const cleanPhone = paymentPhone.replace(/\D/g, "");
      const op = getOperatorFromPrefix(cleanPhone.substring(0, 2));
      const appId = op === "moov"
        ? coreController.appSettingResponse.idFtxMoov
        : coreController.appSettingResponse.idFtxOrange;

      if (!appId) {
        showNotification("Configuration opérateur non disponible. Veuillez rafraîchir.");
        setIsProcessing(false);
        return;
      }

      // Bug #4 fix: partner_commercial_id storage was dead code (no reader
      // anywhere). Use the subdomain directly as the commercial_id signal.
      const urlCommercialId = getCommercialIdFromUrl();

      const response = await directusService.createPaymentIntent({
        app_id: appId,
        mob_app_id: selectedAccount.id,
        amount: Number(amount),
        phonenumber: cleanPhone,
        userID: accountId.trim(),
        commercial_id: urlCommercialId || coreController.commercialId || undefined,
      });

      setPaymentIntentId(response.paymentIntent);
      analyticsService.trackEvent("payment_intent_created", {
        amount: Number(amount),
        operator: op || "orange",
      });
    } catch (error: any) {
      console.error("Error creating payment intent:", error);
      showNotification(error.response?.data?.error || "Une erreur s'est produite lors de la création du paiement");
      setPaymentFlow("error");
      cleanupTimer();
      analyticsService.trackError(error, "payment_intent_creation");
    } finally {
      setIsProcessing(false);
    }
  };

  // ---- Withdraw flow ----
  const handleWithdrawSubmit = async () => {
    if (!selectedAccount || !canSubmitWithdraw) return;
    setIsWithdrawLoading(true);

    try {
      const authResult = await coreController.checkWithdrawAuthorization(withdrawCode, selectedAccount.id);

      if (authResult.status === "blocked") {
        showNotification(authResult.message || "Retrait momentanément indisponible. Veuillez réessayer plus tard.");
        setIsWithdrawLoading(false);
        return;
      }

      if (authResult.status === "deferred") {
        await alertService.sendInfoAlert({
          title: "Retrait différé en attente",
          message: `Retrait différé pour le compte ${selectedAccount.type}. Montant dépasse le seuil autorisé. User: ${accountId}, Phone: ${withdrawPhone}`,
          context: { amount: Number(amount), accountType: selectedAccount.type },
        });
        showNotification("Votre demande de retrait a été prise en compte et sera traitée ultérieurement.", "success");
        analyticsService.trackEvent("withdrawal_deferred", {
          amount: Number(amount),
          accountType: selectedAccount.type,
        });
        setWithdrawFlow("success");
        return;
      }

      await analyticsService.trackWithdrawalInitiated({
        amount: Number(amount),
        accountType: selectedAccount.type,
        commercialId: coreController.commercialId || undefined,
      });

      await directusService.processWithdrawal({
        code: withdrawCode,
        user_id: accountId.trim(),
        phone: withdrawPhone.replace(/\D/g, ""),
        name: withdrawName.trim(),
        compte_id: selectedAccount.id,
      });

      showNotification("Demande de retrait initiée avec succès !", "success");
      analyticsService.trackEvent("withdrawal_success", {
        amount: Number(amount),
        accountType: selectedAccount.type,
      });
      setWithdrawFlow("success");
    } catch (error: any) {
      console.error("Error during withdrawal:", error);
      showNotification(error.message || "Une erreur s'est produite lors du retrait");
      analyticsService.trackError(error, "withdraw_page_processing");
      setWithdrawFlow("error");
    } finally {
      setIsWithdrawLoading(false);
    }
  };

  const resetWithdrawForm = () => {
    setWithdrawCode("");
    setShowHowto(false);
  };

  const resetWithdrawFlow = () => {
    setWithdrawFlow("form");
    resetWithdrawForm();
  };

  // ---- Navigation / support ----
  const handleSupport = () => {
    const whatsappUrl = coreController.appSettingResponse.whatsapp_support_link || "https://wa.me/22605756806";
    window.open(whatsappUrl, "_blank");
  };

  const handleCloseBanner = () => {
    setShowReferralBanner(false);
    localStorage.setItem("referralBannerClosed", "true");
  };

  const handleLogout = async () => {
    try {
      await signOutUser();
      navigate("/login");
    } catch (error) {
      console.error("Logout error:", error);
    }
  };

  const whatsappUrl =
    coreController.appSettingResponse.whatsapp_support_link ||
    `https://wa.me/22605756806?text=${encodeURIComponent(
      `Bonjour, j'ai besoin d'aide pour un ${type === "deposit" ? "dépôt" : "retrait"} sur ${selectedAccount?.type || "1XBET"}.`
    )}`;

  // ---- Onboarding ----
  const finishOnboarding = (startTour: boolean) => {
    if (onboardingSubmitting) return;
    setOnboardingSubmitting(true);
    localStorage.setItem("es.onboarded", "1");
    if (onboardingPhone) localStorage.setItem("es.phone", onboardingPhone);

    const notifyTimer = setTimeout(async () => {
      try {
        await notifyNewUserOnboarding({
          commercialId: commercialId || "affilate",
          whatsappNumber: onboardingPhone,
          displayName: onboardingName,
        } as any);
      } catch (e) {
        console.error("notify onboarding failed", e);
      }
    }, 1000);

    setStartTourAfterOnboard(startTour);
    setOnboardingStep("success");

    const closeTimer = setTimeout(() => {
      setShowOnboard(false);
      setOnboardingSubmitting(false);
      clearTimeout(notifyTimer);
      if (startTour) {
        setTourStep(0);
      }
    }, 1500);

    return () => {
      clearTimeout(notifyTimer);
      clearTimeout(closeTimer);
    };
  };

  const normalizeOnboardingPhone = (value: string) => value.replace(/\D/g, "").slice(0, 15);

  // ---- Render helpers ----
  const cleanPaymentPhone = paymentPhone.replace(/\D/g, "");
  const paymentOperatorKey = getOperatorFromPrefix(cleanPaymentPhone.substring(0, 2)) || "orange";
  const operatorConfig = OperatorConfig[paymentOperatorKey];
  const ussdCode = operatorConfig ? operatorConfig.ussdCode(Number(amount)) : "";
  const timerColor = remainingSeconds <= 60 ? "#ef4444" : theme.chip;
  const hasAutoDialedRef = useRef(false);

  // Auto-launch USSD call 3 seconds after the payment screen appears
  // so the user has time to read the amount before the phone dialer opens.
  // Keeps a manual fallback button in case the auto-dial is blocked.
  useEffect(() => {
    if (paymentFlow !== "payment" || !ussdCode || hasAutoDialedRef.current) return;

    hasAutoDialedRef.current = true;
    const timer = setTimeout(() => {
      window.location.href = `tel:${encodeURIComponent(ussdCode)}`;
    }, 3000);

    return () => clearTimeout(timer);
  }, [paymentFlow, ussdCode]);

  if (isLoadingBookmakers) {
    return (
      <div
        className="flex min-h-[100dvh] flex-col items-center justify-center px-5 text-foreground"
        style={{ background: `radial-gradient(120% 80% at 50% 0%, ${theme.via} 0%, ${theme.from} 45%, ${theme.to} 100%)` }}
      >
        <div className="flex gap-2 mb-8">
          {[0, 1, 2].map((i) => (
            <motion.div key={i} className="h-4 w-4 rounded-full bg-white" animate={{ y: [0, -15, 0] }} transition={{ duration: 0.6, repeat: Infinity, delay: i * 0.15 }} />
          ))}
        </div>
        <p className="text-lg font-medium mb-2">Chargement de l'application...</p>
        <p className="text-sm text-muted-foreground text-center">Veuillez patienter pendant que nous chargeons les plateformes disponibles</p>
      </div>
    );
  }

  return (
    <div className="relative flex min-h-[100dvh] flex-col overflow-y-auto text-foreground antialiased [padding-top:env(safe-area-inset-top)] [padding-bottom:env(safe-area-inset-bottom)] lg:block lg:h-auto lg:min-h-[100dvh]">
      {showConfetti && (
        <Confetti width={window.innerWidth} height={window.innerHeight} recycle={false} numberOfPieces={400} />
      )}

      {/* Dynamic per-bookmaker background */}
      <motion.div
        key={selectedAccount?.id || "default"}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.6 }}
        aria-hidden
        className="pointer-events-none fixed inset-0 -z-10"
        style={{ background: `radial-gradient(120% 80% at 50% 0%, ${theme.via} 0%, ${theme.from} 45%, ${theme.to} 100%)` }}
      />
      <div
        aria-hidden
        className="pointer-events-none fixed inset-x-0 top-0 -z-10 h-[60vh] opacity-40 mix-blend-screen"
        style={{ background: `radial-gradient(60% 50% at 50% 0%, ${theme.chip}33 0%, transparent 70%)` }}
      />

      {/* Header */}
      <header className="sticky top-0 z-30 shrink-0 border-b border-border bg-header-bg backdrop-blur-xl">
        <div className="mx-auto flex max-w-[52ch] items-center justify-between px-4 py-[clamp(0.4rem,1.2vh,0.75rem)] lg:max-w-6xl lg:px-8 lg:py-3">
          <div className="flex min-w-0 items-center gap-2.5 text-foreground">
            <div className="size-9 shrink-0 overflow-hidden rounded-xl sm:size-10" style={{ boxShadow: `0 0 0 1px ${theme.chip}55` }}>
              <img src={easyserviceLogo} alt="EASYSERVICE EXPRESS" className="size-full object-contain" />
            </div>
            <div className="min-w-0">
              <p className="truncate text-sm font-semibold tracking-tight sm:text-base">EASYSERVICE EXPRESS</p>
              <p className="truncate text-[10px] uppercase tracking-widest text-muted-foreground" style={{ color: `${theme.chip}cc` }}>
                Depot & retrait 
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={toggleTheme}
              aria-label={uiTheme === "dark" ? "Passer en mode clair" : "Passer en mode sombre"}
              className="grid size-9 shrink-0 place-items-center rounded-full bg-muted text-foreground ring-1 ring-card-muted-ring transition hover:scale-105 sm:size-10"
            >
              {uiTheme === "dark" ? <Sun className="size-4" /> : <Moon className="size-4" />}
            </button>
            <button
              onClick={() => setMenuOpen(true)}
              aria-label="Menu"
              className="grid size-9 shrink-0 place-items-center rounded-full bg-muted text-foreground ring-1 ring-card-muted-ring transition hover:scale-105 sm:size-10"
            >
              <Menu className="size-4" />
            </button>
          </div>
        </div>
      </header>

      {/* Referral banner */}
      {showReferralBanner && !newUser && (
        <motion.div initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -20 }} className="shrink-0 border-b border-border bg-muted">
          <div className="mx-auto flex max-w-[52ch] items-center gap-2.5 px-4 py-2 lg:max-w-6xl lg:px-8">
            <span className="grid size-6 shrink-0 place-items-center rounded-full" style={{ background: `${theme.chip}33`, color: theme.chip }}>
              <Gift className="size-3.5" />
            </span>
            <p className="grow truncate text-xs font-medium text-foreground/85">
              Gagnez de l'argent avec vos amis ! Parrainez-les et gagnez sur leurs recharges.
            </p>
            <button
              onClick={() => { navigate("/register"); analyticsService.trackEvent("go-register-refferal"); handleCloseBanner(); }}
              className="shrink-0 rounded-full bg-foreground px-2.5 py-1 text-[10px] font-bold text-background"
            >
              Commencer
            </button>
            <button onClick={handleCloseBanner} aria-label="Fermer" className="text-muted-foreground hover:text-foreground">
              <X className="size-3.5" />
            </button>
          </div>
        </motion.div>
      )}

      <main className="mx-auto flex w-full max-w-[52ch] min-h-0 flex-1 flex-col px-3 py-2 sm:px-4 sm:py-3 lg:grid lg:max-w-6xl lg:flex-none lg:grid-cols-[1fr_minmax(0,28rem)] lg:items-start lg:gap-12 lg:px-8 lg:py-6">
        {/* Desktop marketing panel */}
        <aside className="hidden text-foreground lg:sticky lg:top-24 lg:flex lg:flex-col lg:gap-8">
          <div>
            <h1 className="text-balance text-5xl font-semibold leading-[1.05] tracking-tighter">
              Recharge et retrait,<br />
              <span className="text-muted-foreground">en moins de 30s.</span>
            </h1>
            <p className="mt-4 max-w-md text-pretty text-sm leading-relaxed text-muted-foreground">
              EASYSERVICE EXPRESS — une interface unique pour 1XBET, MELBET et BETWINNER. Paiements Orange Money & Moov Money, service client réactif. C'est un portefeuille de paiement mobile : recharge ton compte joueur, retire tes gains, en moins de 30 secondes.
            </p>
          </div>
          <div className="grid grid-cols-3 gap-3">
            {[
              { k: "12K+", v: "Transactions / jour" },
              { k: "28s", v: "Résolution moyenne" },
              { k: "99.9%", v: "Disponibilité" },
            ].map((s) => (
              <div key={s.v} className="rounded-2xl bg-card-muted p-4 ring-1 ring-card-muted-ring backdrop-blur">
                <div className="text-xl font-semibold tracking-tight">{s.k}</div>
                <div className="mt-1 text-[10px] uppercase tracking-widest text-muted-foreground">{s.v}</div>
              </div>
            ))}
          </div>
        </aside>

        <div className="flex min-h-0 min-w-0 flex-1 flex-col lg:block">
          {/* Platform segmented selector — inspiré NafaCash, full width */}
          <section
            className={`mb-[clamp(0.4rem,1vh,0.75rem)] shrink-0 rounded-2xl transition ${
              tourStep === 0 ? "ring-2 ring-offset-2 ring-offset-transparent" : ""
            }`}
            style={tourStep === 0 ? { boxShadow: `0 0 0 2px ${theme.chip}` } : undefined}
          >
            <div className="grid grid-cols-3 gap-1.5 rounded-2xl bg-card-muted p-1 ring-1 ring-card-muted-ring">
              {accounts.map((account) => {
                const t = PLATFORM_THEME[account.type] || DEFAULT_THEME;
                const isSelected = selectedAccount?.id === account.id;
                const accent = PLATFORM_ACCENT[account.type as "1XBET" | "MELBET" | "BETWINNER"] || t.chip;
                const parts = PLATFORM_LABEL_SPLIT[account.type] || { prefix: account.type, suffix: "" };
                return (
                  <button
                    key={account.id}
                    onClick={() => handleAccountSelect(account)}
                    style={
                      isSelected
                        ? { background: accent, color: "#fff", boxShadow: `0 6px 20px -8px ${accent}` }
                        : undefined
                    }
                    className={`rounded-xl py-2 text-[13px] font-black tracking-wider transition ${
                      isSelected ? "" : "text-muted-foreground hover:bg-muted hover:text-foreground"
                    }`}
                  >
                    {isSelected ? (
                      account.type
                    ) : (
                      <>
                        <span className="text-muted-foreground">{parts.prefix}</span>
                        <span style={{ color: accent }}>{parts.suffix}</span>
                      </>
                    )}
                  </button>
                );
              })}
            </div>
          </section>

          {/* Card */}
          <motion.div layout className="flex min-h-0 flex-1 flex-col rounded-2xl bg-card p-3 shadow-2xl ring-1 ring-black/5 sm:rounded-3xl sm:p-5 lg:block">
            {/* Amount hero */}
            <div className={`mb-2 shrink-0 rounded-2xl text-center transition sm:mb-3 ${tourStep === 1 ? "p-2 ring-2 ring-foreground/80 sm:p-3 rounded-2xl" : ""}`}>
              <span className="text-[10px] font-semibold uppercase tracking-[0.18em] text-muted-foreground">
                Montant à {type === "deposit" ? "déposer" : "retirer"}
              </span>
              <div className="mt-0.5 flex items-baseline justify-center gap-2">
                <motion.span
                  key={amount}
                  initial={{ y: 4, opacity: 0.6 }}
                  animate={{ y: 0, opacity: 1 }}
                  className="text-[clamp(1.75rem,6vh,2.5rem)] font-semibold leading-none tracking-tighter tabular-nums"
                >
                  {formatAmount(amount)}
                </motion.span>
                <span className="text-sm font-medium text-muted-foreground">FCFA</span>
              </div>
              <div className="mt-1.5 flex flex-wrap justify-center gap-1.5">
                {QUICK.map((q) => (
                  <button
                    key={q}
                    onClick={() => setAmount(String(q))}
                    className="rounded-full bg-accent-brand-soft px-2.5 py-1 text-[11px] font-semibold text-accent-brand ring-1 ring-card-muted-ring transition hover:bg-accent-brand hover:text-white"
                  >
                    +{q.toLocaleString("fr-FR")}
                  </button>
                ))}
              </div>
            </div>

            {/* Numpad */}
            <div className="mb-2 grid shrink-0 grid-cols-3 gap-2 sm:mb-3">
              {["1", "2", "3", "4", "5", "6", "7", "8", "9", "00", "0", "del"].map((k) => (
                <button
                  key={k}
                  onClick={() => pressKey(k)}
                  className="flex h-14 items-center justify-center rounded-xl bg-surface text-base font-medium tabular-nums ring-1 ring-border transition active:scale-95 active:bg-border sm:h-16 sm:text-lg"
                >
                  {k === "del" ? <Delete className="size-4" /> : k}
                </button>
              ))}
            </div>

            {/* Rotating tip - hidden on very small screens to avoid DOM/removeChild issues */}
            {showTip && (
              <div className="mb-2 h-5 shrink-0 overflow-hidden sm:h-6">
                <AnimatePresence mode="wait">
                  <motion.div
                    key={`${type}-${tipIdx}`}
                    initial={{ y: 14, opacity: 0 }}
                    animate={{ y: 0, opacity: 1 }}
                    exit={{ y: -14, opacity: 0 }}
                    transition={{ duration: 0.35 }}
                    className="flex h-5 items-center justify-center text-center text-[11px] leading-tight text-muted-foreground sm:h-6"
                  >
                    {TIPS[type][tipIdx]}
                  </motion.div>
                </AnimatePresence>
              </div>
            )}

            {/* Bonus EASYSERVICE compact on home card — one per transaction type */}
            {amountOk && (
              (() => {
                const depositFactor = selectedAccount?.deposit_bonus_factor;
                const withdrawFactor = selectedAccount?.withdraw_bonus_factor;
                const hasBonusEnabled = (depositFactor != null && depositFactor > 1) || (withdrawFactor != null && withdrawFactor > 1);
                if (!hasBonusEnabled) return null;

                const depositBonus = calculateBonus(amount, "deposit", depositFactor);
                const withdrawBonus = calculateBonus(amount, "withdraw", withdrawFactor);
                const hasAnyBonus = depositBonus > 0 || withdrawBonus > 0;
                if (hasAnyBonus) {
                  return (
                    <div className="mb-2 shrink-0 grid grid-cols-2 gap-3">
                      <div className="flex items-center justify-center gap-1.5 rounded-full bg-accent-brand-soft px-2 py-1 text-[11px] font-medium">
                        <Gift className="size-3 shrink-0" style={{ color: theme.chip }} />
                        <span className="truncate text-muted-foreground">Dépôt</span>
                        <span className="font-bold tabular-nums" style={{ color: theme.chip }}>
                          +{formatAmount(depositBonus)}
                        </span>
                      </div>
                      <div className="flex items-center justify-center gap-1.5 rounded-full bg-accent-brand-soft px-2 py-1 text-[11px] font-medium">
                        <Gift className="size-3 shrink-0" style={{ color: theme.chip }} />
                        <span className="truncate text-muted-foreground">Retrait</span>
                        <span className="font-bold tabular-nums" style={{ color: theme.chip }}>
                          +{formatAmount(withdrawBonus)}
                        </span>
                      </div>
                    </div>
                  );
                }
                return (
                  <div className="mb-2 shrink-0 flex items-center justify-center gap-1.5 rounded-full bg-muted px-3 py-1 text-[11px] font-medium text-muted-foreground">
                    <Gift className="size-3 shrink-0" />
                    <span>Bonus disponible à partir de {ThemeAppConstants.bonusMinAmount.toLocaleString()} FCFA</span>
                  </div>
                );
              })()
            )}

            {/* Primary actions */}
            <div className="mt-auto grid grid-cols-2 gap-3">
              <button
                onClick={() => startFlow("deposit")}
                style={{ background: (selectedAccount?.type && PLATFORM_ACCENT[selectedAccount.type as "1XBET" | "MELBET" | "BETWINNER"]) || theme.chip, color: "#fff" }}
                className={`flex w-full shrink-0 items-center justify-center gap-2 rounded-2xl bg-foreground py-3.5 text-sm font-semibold text-background shadow-sm transition enabled:hover:opacity-90 enabled:active:scale-[0.99] ${tourStep === 2 ? "ring-4 ring-offset-2 ring-foreground/70 ring-offset-card" : ""}`}
              >
                Dépôt
                <ArrowRight className="size-4" />
              </button>
              <button
                onClick={() => startFlow("withdraw")}
                className="flex w-full shrink-0 items-center justify-center gap-2 rounded-2xl bg-foreground py-3.5 text-sm font-semibold text-background shadow-sm transition enabled:hover:opacity-90 enabled:active:scale-[0.99]"
              >
                Retrait
                <ArrowDownRight className="size-4" />
              </button>
            </div>
          </motion.div>
        </div>
      </main>

      {/* Deposit / Withdraw bottom sheet */}
      <BottomSheet open={showForm} onOpenChange={(open) => { if (!open) { setShowForm(false); if (paymentFlow !== "form") resetPaymentFlow(); if (withdrawFlow !== "form") resetWithdrawFlow(); } else setShowForm(true); }}>
        <BottomSheetContent className="bg-card text-foreground">
          <BottomSheetTitle className="sr-only">Formulaire {type === "deposit" ? "dépôt" : "retrait"}</BottomSheetTitle>
          <BottomSheetDescription className="sr-only">Renseignez vos informations pour finaliser l'opération.</BottomSheetDescription>
          <div className="mx-auto flex h-full w-full max-w-[44ch] flex-col px-0 pb-0 pt-0">
            {/* Header of sheet */}
            <div className="mb-4 flex items-center justify-between">
              <div>
                <p className="text-[10px] font-semibold uppercase tracking-widest text-muted-foreground">
                  {type === "deposit" ? "Dépôt" : "Retrait"} · {selectedAccount?.type}
                </p>
                <p className="mt-0.5 text-xl font-semibold tabular-nums tracking-tight">
                  {formatAmount(amount)} <span className="text-sm text-muted-foreground">FCFA</span>
                </p>
              </div>
              <div className="flex items-center gap-2 text-right">
                <span className="inline-block h-1.5 w-1.5 rounded-full" style={{ background: theme.chip }} aria-hidden />
                <div className="leading-tight">
                  <p className="text-[9px] font-medium uppercase tracking-[0.18em] text-muted-foreground">{type === "withdraw" ? "Mode" : "Règlement"}</p>
                  <p className="text-[11px] font-semibold tracking-tight text-foreground">{type === "withdraw" ? "Sans paiement" : "Orange · Moov"}</p>
                </div>
              </div>
            </div>

            {/* Bonus EASYSERVICE summary */}
            {(() => {
              const bonusFactor = type === "deposit" ? selectedAccount?.deposit_bonus_factor : selectedAccount?.withdraw_bonus_factor;
              const hasBonusEnabled = bonusFactor != null && bonusFactor > 1;
              if (!hasBonusEnabled) return null;

              const bonus = calculateBonus(amount, type, bonusFactor);
              return (
                <div className="mb-4 rounded-2xl bg-surface p-3.5 ring-1 ring-border">
                  <div className="flex items-center justify-between">
                    <span className="text-[11px] font-medium text-muted-foreground">
                      {type === "deposit" ? "Vous déposez" : "Retrait demandé"}
                    </span>
                    <span className="text-sm font-bold text-foreground tabular-nums">
                      {formatAmount(amount)} FCFA
                    </span>
                  </div>
                  {(() => {
                    if (bonus > 0) {
                      return (
                        <>
                          <div className="my-2 h-px bg-border" />
                          <div className="flex items-center justify-between">
                            <span className="flex items-center gap-1.5 text-[11px] font-medium text-foreground">
                              <Gift className="size-3.5" style={{ color: theme.chip }} />
                              Bonus EASYSERVICE
                            </span>
                            <span className="text-sm font-bold tabular-nums" style={{ color: theme.chip }}>
                              +{formatAmount(bonus)} FCFA
                            </span>
                          </div>
                          <div className="my-2 h-px bg-border" />
                          <div className="flex items-center justify-between">
                            <span className="text-xs font-semibold text-foreground">
                              Vous recevez
                            </span>
                            <span className="text-base font-bold tabular-nums" style={{ color: theme.chip }}>
                              {formatAmount(calculateTotalWithBonus(amount, type, bonusFactor))} FCFA
                            </span>
                          </div>
                        </>
                      );
                    }
                    return (
                      <>
                        <div className="my-2 h-px bg-border" />
                        <div className="flex items-center gap-1.5 rounded-lg bg-muted/60 px-2.5 py-2">
                          <Gift className="size-3.5 shrink-0 text-muted-foreground" />
                          <p className="text-[11px] leading-snug text-muted-foreground">
                            Bonus EASYSERVICE disponible à partir de {ThemeAppConstants.bonusMinAmount.toLocaleString()} FCFA.
                          </p>
                        </div>
                        <div className="my-2 h-px bg-border" />
                        <div className="flex items-center justify-between">
                          <span className="text-xs font-semibold text-foreground">
                            Vous recevez
                          </span>
                          <span className="text-base font-bold tabular-nums text-foreground">
                            {formatAmount(amount)} FCFA
                          </span>
                        </div>
                      </>
                    );
                  })()}
                </div>
              );
            })()}

            {/* DEPOSIT FORM */}
            {type === "deposit" && paymentFlow === "form" && (
              <div className="space-y-3">
                <Field label={`ID Compte Joueur ${selectedAccount?.type}`}>
                  <div className="relative">
                    <div className="absolute left-3 top-1/2 -translate-y-1/2 rounded-lg p-2" style={{ backgroundColor: `${theme.chip}1A` }}>
                      <User className="size-4" style={{ color: theme.chip }} />
                    </div>
                    <input
                      value={accountId}
                      onChange={(e) => setAccountId(e.target.value)}
                      inputMode="numeric"
                      placeholder="Ex : 1687738063"
                      className="w-full rounded-xl bg-surface py-3 pl-12 pr-4 text-base tabular-nums ring-1 ring-border outline-none transition focus:ring-foreground/20"
                    />
                  </div>
                </Field>

                {accountId.trim().length >= 3 && (
                  <div
                    className={`rounded-xl px-4 py-3 text-sm ring-1 transition ${userNameStatus === "found"
                      ? "bg-surface ring-border"
                      : userNameStatus === "notfound"
                        ? "border-destructive/40 bg-destructive/10 text-destructive ring-destructive/30"
                        : "bg-surface ring-border"
                      }`}
                  >
                    <div className="text-[10px] font-semibold uppercase tracking-widest text-muted-foreground">
                      Nom du titulaire ({selectedAccount?.type})
                    </div>
                    {userNameStatus === "loading" && (
                      <div className="mt-1 flex items-center gap-2 text-muted-foreground">
                        <span className="size-3 animate-spin rounded-full border-2 border-muted-foreground/30 border-t-foreground" />
                        Vérification…
                      </div>
                    )}
                    {userNameStatus === "found" && (
                      <div className="mt-0.5 flex items-center gap-2">
                        <CheckCircle2 className="size-4" style={{ color: theme.chip }} />
                        <span className="text-base font-semibold uppercase tracking-wide text-foreground">{resolvedName}</span>
                      </div>
                    )}
                    {userNameStatus === "notfound" && (
                      <div className="mt-0.5 text-[12px] font-medium">Aucun compte {selectedAccount?.type} trouvé pour cet ID. Vérifie le ID ou bookmaker choisie.</div>
                    )}
                  </div>
                )}

                <div className="flex gap-2.5">
                  <div className="w-28 shrink-0">
                    <Field label="Opérateur">
                      <select
                        value={operator}
                        onChange={(e) => setOperator(e.target.value as Operator)}
                        className="w-full appearance-none rounded-xl bg-surface px-3 py-3 text-sm font-medium ring-1 ring-border outline-none"
                      >
                        <option>Orange</option>
                        <option>Moov</option>
                      </select>
                    </Field>
                  </div>
                  <div className="min-w-0 grow">
                    <Field label="Numéro mobile (qui paye)">
                      <div className="relative">
                        <div className="absolute left-3 top-1/2 -translate-y-1/2 rounded-lg p-2" style={{ backgroundColor: `${theme.chip}1A` }}>
                          <Phone className="size-4" style={{ color: theme.chip }} />
                        </div>
                        <input
                          value={paymentPhone}
                          onChange={(e) => setPaymentPhone(e.target.value)}
                          inputMode="tel"
                          maxLength={8}
                          placeholder="07 00 00 00 00"
                          className="w-full rounded-xl bg-surface py-3 pl-12 pr-4 text-base tabular-nums ring-1 ring-border outline-none transition focus:ring-foreground/20"
                        />
                      </div>
                    </Field>
                  </div>
                </div>

                {paymentPhone.replace(/\D/g, "").length > 0 && paymentPhone.replace(/\D/g, "").length !== 8 && (
                  <p className="text-[11px] text-destructive">Le numéro doit contenir exactement 8 chiffres.</p>
                )}

                <button
                  disabled={!canSubmitDeposit || isProcessing}
                  onClick={handleStartDeposit}
                  style={canSubmitDeposit ? { background: theme.chip, color: "#fff" } : undefined}
                  className="mt-2 flex w-full items-center justify-center gap-2 rounded-2xl bg-foreground py-3.5 text-sm font-semibold text-background shadow-sm transition disabled:cursor-not-allowed disabled:opacity-40 enabled:hover:opacity-90 enabled:active:scale-[0.99]"
                >
                  {isProcessing ? <Loader2 className="size-4 animate-spin" /> : <ArrowRight className="size-4" />}
                  {isProcessing ? "Vérification..." : "Confirmer le dépôt"}
                </button>
              </div>
            )}

            {/* PAYMENT SCREEN */}
            {type === "deposit" && paymentFlow === "payment" && (
              <div className="flex h-full flex-col justify-between gap-2">
                <div className="space-y-2">
                  {/* Timer */}
                  <div className="flex justify-center">
                    <div
                      className="inline-flex items-center gap-2 rounded-xl border px-3 py-1.5"
                      style={{
                        backgroundColor: remainingSeconds <= 60 ? "#ef44441A" : `${theme.chip}1A`,
                        borderColor: remainingSeconds <= 60 ? "#ef44444D" : `${theme.chip}4D`,
                      }}
                    >
                      <Clock className="size-4" style={{ color: timerColor }} />
                      <span className="text-xs text-muted-foreground">Session expire dans :</span>
                      <span className="text-sm font-bold" style={{ color: timerColor }}>
                        {formatTime(remainingSeconds)}
                      </span>
                    </div>
                  </div>

                  {/* Header card */}
                  <div className="rounded-2xl p-3 text-foreground" style={{ background: `linear-gradient(135deg, ${theme.from}, ${theme.via})` }}>
                    <div className="flex items-start justify-between">
                      <div>
                        <p className="text-[10px] uppercase tracking-widest text-muted-foreground">Montant à payer</p>
                        <p className="text-xl font-bold">{formatAmount(amount)} FCFA</p>
                      </div>
                      <div className="rounded-2xl bg-muted p-2">
                        <Wallet className="size-5 text-foreground" />
                      </div>
                    </div>
                    <div className="mt-2 rounded-xl bg-muted p-2 text-[11px] leading-relaxed">
                      Dépôt de {formatAmount(amount)} FCFA sur le compte <b>{accountId}</b> appartenant à{" "}
                      <span className="font-bold text-red-300">{resolvedName}</span>
                    </div>
                  </div>

                  {/* Tracking status */}
                  <div
                    className="rounded-xl border p-2.5"
                    style={{
                      backgroundColor: isPollingMode ? "#0052cc1A" : `${theme.chip}1A`,
                      borderColor: isPollingMode ? "#0052cc4D" : `${theme.chip}4D`,
                    }}
                  >
                    <div className="flex items-center gap-2">
                      <Loader2 className="size-4 animate-spin" style={{ color: isPollingMode ? "#0052cc" : theme.chip }} />
                      <p className="text-xs font-semibold" style={{ color: isPollingMode ? "#002366" : theme.chip }}>
                        {trackingStatus}
                      </p>
                    </div>
                  </div>
                </div>

                {/* USSD card */}
                <div className="rounded-xl bg-surface p-3 ring-1 ring-border">
                  <div className="mb-2 flex items-center gap-2 text-muted-foreground">
                    <Smartphone className="size-4" style={{ color: theme.chip }} />
                    <span className="text-xs font-medium">Appel lancé. Vérifie ton téléphone.</span>
                  </div>
                  <button
                    onClick={() => {
                      window.location.href = `tel:${encodeURIComponent(ussdCode)}`;
                    }}
                    className="flex w-full items-center justify-between rounded-lg border border-border bg-card p-2.5 transition hover:bg-border/40"
                  >
                    <p className="text-base font-semibold tracking-wide text-foreground">{ussdCode}</p>
                    <div className="rounded-full p-2" style={{ backgroundColor: `${operatorConfig.color}1A` }}>
                      <Phone className="size-[18px]" style={{ color: operatorConfig.color }} />
                    </div>
                  </button>
                  <button
                    onClick={() => copyToClipboard(ussdCode)}
                    className="mt-2 flex w-full items-center justify-center gap-1 rounded-lg border border-border py-2 text-xs font-medium text-muted-foreground transition hover:text-foreground"
                  >
                    <Copy className="size-3.5" /> Copier le code USSD
                  </button>
                </div>

                <div className="flex items-center justify-center gap-2 rounded-xl bg-surface p-2 ring-1 ring-border">
                  <Shield className="size-4 text-green-600" />
                  <span className="text-[11px] text-muted-foreground">
                    Secured by <b>Futurix</b> <b className="text-green-600">Pay</b>
                  </span>
                </div>
              </div>
            )}

            {/* PAYMENT SUCCESS */}
            {type === "deposit" && paymentFlow === "success" && (
              <div className="space-y-4 py-6 text-center">
                <div className="mx-auto grid size-16 place-items-center rounded-full bg-green-500">
                  <CheckCircle2 className="size-8 text-foreground" />
                </div>
                <h3 className="text-lg font-semibold">Paiement confirmé !</h3>
                <p className="text-sm text-muted-foreground">Votre compte sera crédité dans quelques instants.</p>
                <button
                  onClick={() => { setShowForm(false); resetPaymentFlow(); }}
                  style={{ background: theme.chip, color: "#fff" }}
                  className="w-full rounded-2xl py-3 text-sm font-semibold"
                >
                  Retour à l'accueil
                </button>
              </div>
            )}

            {/* PAYMENT ERROR */}
            {type === "deposit" && paymentFlow === "error" && (
              <div className="space-y-4 py-6 text-center">
                <div className="mx-auto grid size-16 place-items-center rounded-full bg-amber-500">
                  <AlertCircle className="size-8 text-white" />
                </div>
                <h3 className="text-lg font-semibold">Paiement en attente de confirmation</h3>
                <p className="text-sm text-muted-foreground">Votre paiement a peut-être été effectué sans que nous l'ayons détecté automatiquement. Vérifiez votre solde avant de réessayer.</p>
                <button
                  onClick={() => { if (paymentIntentId) { setPaymentFlow("payment"); startSessionTimer(); } else { resetPaymentFlow(); } }}
                  className="w-full rounded-2xl bg-foreground py-3 text-sm font-semibold text-background"
                >
                  Réessayer
                </button>
                <button
                  onClick={handleSupport}
                  className="w-full rounded-2xl bg-surface py-3 text-sm font-medium text-foreground ring-1 ring-border transition hover:bg-border/40 flex items-center justify-center gap-2"
                >
                  <MessageCircle className="size-4" /> Contacter le support
                </button>
              </div>
            )}

            {/* WITHDRAW FORM */}
            {type === "withdraw" && withdrawFlow === "form" && (
              <div className="space-y-3">
                <Field label={`ID ${selectedAccount?.type}`}>
                  <div className="relative">
                    <div className="absolute left-3 top-1/2 -translate-y-1/2 rounded-lg p-2" style={{ backgroundColor: `${theme.chip}1A` }}>
                      <User className="size-4" style={{ color: theme.chip }} />
                    </div>
                    <input
                      value={accountId}
                      onChange={(e) => setAccountId(e.target.value)}
                      inputMode="numeric"
                      placeholder="Entrez votre ID de compte"
                      className="w-full rounded-xl bg-surface py-3 pl-12 pr-4 text-base tabular-nums ring-1 ring-border outline-none transition focus:ring-foreground/20"
                    />
                  </div>
                </Field>

                <Field label="Numéro Orange Money (8 chiffres)">
                  <div className="relative">
                    <div className="absolute left-3 top-1/2 -translate-y-1/2 rounded-lg p-2" style={{ backgroundColor: `${theme.chip}1A` }}>
                      <Phone className="size-4" style={{ color: theme.chip }} />
                    </div>
                    <input
                      value={withdrawPhone}
                      onChange={(e) => setWithdrawPhone(e.target.value)}
                      inputMode="tel"
                      maxLength={8}
                      placeholder="07 00 00 00 00"
                      className="w-full rounded-xl bg-surface py-3 pl-12 pr-4 text-base tabular-nums ring-1 ring-border outline-none transition focus:ring-foreground/20"
                    />
                  </div>
                </Field>
                <p className="text-[11px] text-muted-foreground">Les retraits ne sont acceptés que sur les numéros Orange Money.</p>

                <Field label="Nom du compte mobile money">
                  <div className="relative">
                    <div className="absolute left-3 top-1/2 -translate-y-1/2 rounded-lg p-2" style={{ backgroundColor: `${theme.chip}1A` }}>
                      <User className="size-4" style={{ color: theme.chip }} />
                    </div>
                    <input
                      value={withdrawName}
                      onChange={(e) => setWithdrawName(e.target.value)}
                      placeholder="Nom sur le compte Orange money"
                      className="w-full rounded-xl bg-surface py-3 pl-12 pr-4 text-base ring-1 ring-border outline-none transition focus:ring-foreground/20"
                    />
                  </div>
                </Field>

                <Field label="Code de retrait (sur le bookmaker)">
                  <div className="relative">
                    <div className="absolute left-3 top-1/2 -translate-y-1/2 rounded-lg p-2" style={{ backgroundColor: `${theme.chip}1A` }}>
                      <Key className="size-4" style={{ color: theme.chip }} />
                    </div>
                    <input
                      value={withdrawCode}
                      onChange={(e) => setWithdrawCode(e.target.value)}
                      inputMode="text"
                      placeholder="Ex : 4F7K9P2"
                      className="w-full rounded-xl bg-surface py-3 pl-12 pr-4 text-base font-mono uppercase tracking-wider ring-1 ring-border outline-none transition focus:ring-foreground/20"
                    />
                  </div>
                </Field>

                <button
                  type="button"
                  onClick={() => setShowHowto((v) => !v)}
                  className="flex w-full items-center justify-between rounded-xl bg-surface px-3.5 py-2.5 text-left text-xs font-medium text-foreground ring-1 ring-border transition hover:bg-border/40"
                >
                  <span className="flex items-center gap-2">
                    <Info className="size-3.5" style={{ color: theme.chip }} />
                    Où trouver mon code de retrait ?
                  </span>
                  <ChevronDown className={`size-4 text-muted-foreground transition ${showHowto ? "rotate-180" : ""}`} />
                </button>
                <AnimatePresence>
                  {showHowto && (
                    <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }} exit={{ opacity: 0, height: 0 }} className="overflow-hidden">
                      <div className="rounded-xl bg-surface p-3.5 text-[11px] leading-relaxed text-muted-foreground ring-1 ring-border">
                        <ol className="space-y-1.5">
                          <li>
                            <b className="text-foreground">1.</b> Sur {selectedAccount?.type} → <b className="text-foreground">Retrait → Espèces</b>.
                          </li>
                          <li>
                            <b className="text-foreground">2.</b> Renseigne notre point :
                            <div className="mt-1.5 flex items-start gap-2 rounded-lg bg-card p-2.5 ring-1 ring-border">
                              <MapPin className="mt-0.5 size-3.5 shrink-0" style={{ color: theme.chip }} />
                              <div className="text-foreground">
                                <p>
                                  <b>Ville :</b> Ouagadougou
                                </p>
                                <p>
                                  <b>Point :</b>{" "}
                                  {(coreController.appSettingResponse.config?.WITHDRAW_RUE as Record<string, string>)?.[selectedAccount?.type || ""] || "EASYSERVICE EXPRESS"}
                                </p>
                              </div>
                            </div>
                          </li>
                          <li>
                            <b className="text-foreground">3.</b> Récupère le code et colle-le ici.
                          </li>
                        </ol>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>

                <button
                  disabled={!canSubmitWithdraw || isWithdrawLoading}
                  onClick={handleWithdrawSubmit}
                  style={canSubmitWithdraw ? { background: theme.chip, color: "#fff" } : undefined}
                  className="mt-2 flex w-full items-center justify-center gap-2 rounded-2xl bg-foreground py-3.5 text-sm font-semibold text-background shadow-sm transition disabled:cursor-not-allowed disabled:opacity-40 enabled:hover:opacity-90 enabled:active:scale-[0.99]"
                >
                  {isWithdrawLoading ? <Loader2 className="size-4 animate-spin" /> : <Wallet className="size-4" />}
                  {isWithdrawLoading ? "Traitement..." : "Soumettre la demande"}
                </button>
              </div>
            )}

            {/* WITHDRAW SUCCESS */}
            {type === "withdraw" && withdrawFlow === "success" && (
              <div className="space-y-4 py-6 text-center">
                <div className="mx-auto grid size-16 place-items-center rounded-full bg-green-500">
                  <CheckCircle2 className="size-8 text-foreground" />
                </div>
                <h3 className="text-lg font-semibold">Demande de retrait envoyée !</h3>
                <p className="text-sm text-muted-foreground">
                  Votre demande de retrait de {formatAmount(amount)} FCFA a été prise en compte.
                  Vous serez crédité sur votre compte Orange Money sous peu.
                </p>
                <button
                  onClick={() => { setShowForm(false); resetWithdrawFlow(); }}
                  style={{ background: theme.chip, color: "#fff" }}
                  className="w-full rounded-2xl py-3 text-sm font-semibold"
                >
                  Fermer
                </button>
              </div>
            )}

            {/* WITHDRAW ERROR */}
            {type === "withdraw" && withdrawFlow === "error" && (
              <div className="space-y-4 py-6 text-center">
                <div className="mx-auto grid size-16 place-items-center rounded-full bg-destructive">
                  <AlertCircle className="size-8 text-foreground" />
                </div>
                <h3 className="text-lg font-semibold text-destructive">Retrait échoué</h3>
                <p className="text-sm text-muted-foreground">Votre demande de retrait n'a pas pu aboutir. Vérifiez vos informations ou contactez le support.</p>
                <button
                  onClick={() => setWithdrawFlow("form")}
                  className="w-full rounded-2xl bg-foreground py-3 text-sm font-semibold text-background"
                >
                  Réessayer
                </button>
                <button
                  onClick={handleSupport}
                  className="w-full rounded-2xl bg-surface py-3 text-sm font-medium text-foreground ring-1 ring-border transition hover:bg-border/40 flex items-center justify-center gap-2"
                >
                  <MessageCircle className="size-4" /> Contacter le support
                </button>
              </div>
            )}
          </div>
        </BottomSheetContent>
      </BottomSheet>

      {/* Support Widget — boule WhatsApp déplaçable */}
      <button
        onClick={() => {
          if (!waJustMovedRef.current) handleSupport();
          waJustMovedRef.current = false;
        }}
        onPointerDown={(e) => {
          (e.target as HTMLElement).setPointerCapture?.(e.pointerId);
          waDragRef.current = {
            startX: e.clientX,
            startY: e.clientY,
            origX: waPos.x,
            origY: waPos.y,
            moved: false,
          };
          waJustMovedRef.current = false;
        }}
        onPointerMove={(e) => {
          if (!waDragRef.current) return;
          const dx = e.clientX - waDragRef.current.startX;
          const dy = e.clientY - waDragRef.current.startY;
          if (Math.abs(dx) > 4 || Math.abs(dy) > 4) waDragRef.current.moved = true;
          if (!waDragRef.current.moved) return;
          const maxX = window.innerWidth - 56;
          const maxY = window.innerHeight - 56;
          const newX = Math.max(8, Math.min(maxX, waDragRef.current.origX - dx));
          const newY = Math.max(8, Math.min(maxY, waDragRef.current.origY - dy));
          setWaPos({ x: newX, y: newY });
        }}
        onPointerUp={() => {
          if (waDragRef.current) {
            waJustMovedRef.current = waDragRef.current.moved;
            waDragRef.current = null;
            try {
              window.localStorage.setItem("es.wa.pos", JSON.stringify(waPos));
            } catch {}
          }
        }}
        onPointerCancel={() => {
          waDragRef.current = null;
        }}
        aria-label="Obtenir de l'aide — glisser pour déplacer"
        className="fixed z-40 grid size-12 cursor-grab touch-none select-none place-items-center rounded-full bg-card text-black shadow-lg shadow-black/30 ring-1 ring-black/10 transition active:cursor-grabbing"
        style={{ right: `${waPos.x}px`, bottom: `${waPos.y}px` }}
      >
        <img src="/assets/WhatsApp.svg.png" alt="WhatsApp" className="size-7 pointer-events-none object-contain" />
      </button>

      {/* Quick actions / Menu */}
      <QuickActionDrawer
        action={quickAction}
        onClose={() => setQuickAction(null)}
        platform={selectedAccount?.type || "1XBET"}
        theme={theme}
        whatsappUrl={whatsappUrl}
        onNavigate={navigate}
      />

      <MenuDrawer
        open={menuOpen}
        onOpenChange={setMenuOpen}
        theme={theme}
        whatsappUrl={whatsappUrl}
        isAuthenticated={!!newUser}
        onNavigate={(path) => {
          setMenuOpen(false);
          navigate(path);
        }}
        onOpenQuick={(id) => {
          setMenuOpen(false);
          setQuickAction(id);
        }}
      />

      {/* Onboarding bottom sheet */}
      <BottomSheet open={showOnboard} onOpenChange={(open) => {
        if (!open) {
          // Fermeture via croix/swipe sans valider → ne plus jamais réafficher
          if (!onboardingSubmitting && onboardingStep === "welcome") {
            localStorage.setItem("es.onboarded", "1");
            localStorage.setItem(DISMISSED_KEY, "true");
          }
          setShowOnboard(false);
          setOnboardingStep("welcome");
        }
      }}>
        <BottomSheetContent className="bg-card text-foreground">
          <BottomSheetTitle className="sr-only">Bienvenue sur EASYSERVICE EXPRESS</BottomSheetTitle>
          <BottomSheetDescription className="sr-only">Renseigne ton numéro WhatsApp pour découvrir l'application.</BottomSheetDescription>
          <div className="mx-auto w-full max-w-[44ch]">
            {onboardingStep === "welcome" ? (
              <>
                <div className="mb-4">
                  <p className="text-[10px] font-bold uppercase tracking-widest" style={{ color: theme.chip }}>
                    Bienvenue
                  </p>
                  <h2 className="mt-1 text-xl font-semibold tracking-tight">EASYSERVICE EXPRESS en 30 secondes</h2>
                  <p className="mt-1 text-[12px] leading-relaxed text-muted-foreground">
                    Recharge et retrait sur 1XBET, MELBET, BETWINNER — sans tracas. EASYSERVICE EXPRESS est une
                    application de paiement mobile : tu ajoutes de l'argent sur ton compte de jeu, et tu retires tes
                    gains directement par Orange Money ou Moov Money.
                  </p>
                </div>

                <ul className="mb-4 space-y-2">
                  {["Crédit en moins de 30s", "Support intelligent · auto-résolution", "Aucun mot de passe ne te sera demandé"].map((t) => (
                    <li key={t} className="flex items-center gap-2 text-[12px] text-foreground">
                      <span className="grid size-4 shrink-0 place-items-center rounded-full" style={{ background: `${theme.chip}26`, color: theme.chip }}>
                        <Check className="size-2.5" />
                      </span>
                      {t}
                    </li>
                  ))}
                </ul>

                <div className="space-y-2.5">
                  <Field label="Ton prénom (optionnel)">
                    <input
                      value={onboardingName}
                      onChange={(e) => setOnboardingName(e.target.value)}
                      placeholder="Ex : Aïcha"
                      className="w-full rounded-xl bg-surface px-4 py-3 text-base ring-1 ring-border outline-none focus:ring-foreground/20"
                    />
                  </Field>
                  <Field label="Ton numéro WhatsApp (format international)">
                    <input
                      value={onboardingPhone}
                      onChange={(e) => setOnboardingPhone(normalizeOnboardingPhone(e.target.value))}
                      inputMode="tel"
                      placeholder="22670000000"
                      className="w-full rounded-xl bg-surface px-4 py-3 text-base tabular-nums ring-1 ring-border outline-none focus:ring-foreground/20"
                    />
                    <p className="mt-1 text-[11px] text-muted-foreground">Ex : 22670000000 (indicatif + numéro sans le +)</p>
                  </Field>
                </div>

                <button
                  disabled={onboardingPhone.replace(/\D/g, "").length < 11 || onboardingSubmitting}
                  onClick={() => finishOnboarding(true)}
                  style={onboardingPhone.replace(/\D/g, "").length >= 11 ? { background: theme.chip, color: "#fff" } : undefined}
                  className="mt-4 flex w-full items-center justify-center gap-2 rounded-2xl bg-foreground py-3.5 text-sm font-semibold text-background transition disabled:cursor-not-allowed disabled:opacity-40"
                >
                  {onboardingSubmitting ? "Traitement..." : "Commencer le tour guidé"}
                  {!onboardingSubmitting && <ArrowRight className="size-4" />}
                </button>
                <button onClick={() => finishOnboarding(false)} disabled={onboardingSubmitting} className="mt-2 flex w-full items-center justify-center gap-1 text-center text-[11px] text-muted-foreground hover:text-foreground disabled:opacity-40">
                  Passer pour le moment
                </button>
              </>
            ) : (
              <div className="py-4 text-center">
                <div className="mx-auto mb-3 grid size-14 place-items-center rounded-full" style={{ background: `${theme.chip}26`, color: theme.chip }}>
                  <Sparkles className="size-7" />
                </div>
                <h2 className="text-lg font-semibold">
                  {startTourAfterOnboard ? "C'est parti !" : "Bienvenue !"}
                </h2>
                <p className="mt-1 text-sm text-muted-foreground">
                  {startTourAfterOnboard ? "Le tour guidé démarre maintenant." : "Vous pouvez maintenant utiliser EASYSERVICE EXPRESS."}
                </p>
              </div>
            )}
          </div>
        </BottomSheetContent>
      </BottomSheet>

      {/* Tour guidé */}
      <AnimatePresence>
        {tourStep >= 0 && tourStep < TOUR_STEPS.length && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 20 }}
            className="fixed inset-x-3 bottom-24 z-40 mx-auto max-w-[28rem] rounded-2xl bg-card p-4 shadow-2xl ring-1 ring-border"
          >
            <div className="flex items-start gap-3">
              <span className="grid size-8 shrink-0 place-items-center rounded-full text-xs font-bold" style={{ background: `${theme.chip}26`, color: theme.chip }}>
                {tourStep + 1}
              </span>
              <div className="min-w-0 grow">
                <p className="text-sm font-semibold tracking-tight">{TOUR_STEPS[tourStep].title}</p>
                <p className="mt-0.5 text-[11px] leading-relaxed text-muted-foreground">{TOUR_STEPS[tourStep].desc}</p>
              </div>
              <button onClick={() => setTourStep(-1)} className="text-muted-foreground hover:text-foreground" aria-label="Fermer le tour">
                <X className="size-4" />
              </button>
            </div>
            <div className="mt-3 flex items-center justify-between">
              <div className="flex gap-1">
                {TOUR_STEPS.map((_, i) => (
                  <span key={i} className="h-1 w-6 rounded-full" style={{ background: i <= tourStep ? theme.chip : "hsl(var(--border))" }} />
                ))}
              </div>
              <button
                onClick={() => (tourStep + 1 >= TOUR_STEPS.length ? setTourStep(-1) : setTourStep(tourStep + 1))}
                style={{ background: theme.chip, color: "#fff" }}
                className="rounded-full px-4 py-1.5 text-xs font-semibold"
              >
                {tourStep + 1 >= TOUR_STEPS.length ? "Terminer" : "Suivant"}
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Notification */}
      <Notification message={notification.message} type={notification.type} isVisible={notification.isVisible} onClose={hideNotification} />

      {/* Minimum amount alert modal */}
      <AnimatePresence>
        {minAmountAlert.open && minAmountAlert.flowType && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setMinAmountAlert({ open: false, flowType: null })}
            className="fixed inset-0 z-[70] flex items-center justify-center bg-black/55 p-4"
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0, y: 10 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.9, opacity: 0, y: 10 }}
              transition={{ type: "spring", duration: 0.3 }}
              onClick={(e) => e.stopPropagation()}
              className="w-full max-w-sm rounded-2xl bg-card p-6 text-center shadow-2xl ring-1 ring-border"
            >
              <div className="mx-auto mb-4 grid size-14 place-items-center rounded-full bg-destructive/10">
                <AlertCircle className="size-7 text-destructive" />
              </div>
              <h3 className="text-lg font-bold text-foreground">Montant insuffisant</h3>
              <p className="mt-2 text-sm text-muted-foreground">
                Le montant minimum pour un{" "}
                <span className="font-semibold text-foreground">
                  {minAmountAlert.flowType === "deposit" ? "dépôt" : "retrait"}
                </span>{" "}
                est de{" "}
                <span className="font-bold" style={{ color: theme.chip }}>
                  {(minAmountAlert.flowType === "deposit"
                    ? AppConstants.minAmount
                    : AppConstants.minWithdrawAmount
                  ).toLocaleString("fr-FR")}{" "}
                  FCFA
                </span>
                .
              </p>
              <button
                onClick={() => setMinAmountAlert({ open: false, flowType: null })}
                style={{ background: theme.chip, color: "#fff" }}
                className="mt-5 w-full rounded-xl py-3 text-sm font-semibold transition active:scale-[0.98]"
              >
                Compris
              </button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Barre de navigation bas */}
      <BottomNav accent={theme.chip} />
    </div>
  );
});

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <span className="mb-1 block text-[10px] font-semibold uppercase tracking-widest text-muted-foreground">{label}</span>
      {children}
    </label>
  );
}

function MenuDrawer({
  open,
  onOpenChange,
  theme,
  whatsappUrl,
  isAuthenticated,
  onNavigate,
  onOpenQuick,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  theme: ThemeShape;
  whatsappUrl: string;
  isAuthenticated: boolean;
  onNavigate: (path: string) => void;
  onOpenQuick: (id: QuickActionId) => void;
}) {
  const [submenu, setSubmenu] = useState<"games" | null>(null);

  const items = [
    { id: "pronos", label: "Pronostiquer", icon: TrendingUp, onClick: () => onNavigate("/pronostics") },
    { id: "play", label: "Jouer", icon: Gamepad2, onClick: () => setSubmenu("games") },
    { id: "refer", label: "Parrainer", icon: Users, onClick: () => onNavigate(isAuthenticated ? "/parrainage" : "/login") },
    { id: "more", label: "Services", icon: Grid3x3, onClick: () => onOpenQuick("more") },
  ] as const;

  const games = [
    { label: "Carte à gratter", path: "/scratch-card" },
  ];

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) setSubmenu(null); onOpenChange(v); }}>
      <DialogContent className="max-h-[80vh] border-border bg-card text-foreground">
        <DialogTitle className="sr-only">Menu</DialogTitle>
        <DialogDescription className="sr-only">Accès rapides EASYSERVICE EXPRESS</DialogDescription>
        <div className="mx-auto w-full max-w-[40ch] px-5 pb-7 pt-3">
          <div className="mb-3 flex items-center justify-between">
            <div>
              <h3 className="text-base font-semibold tracking-tight">{submenu === "games" ? "Jeux" : "Menu"}</h3>
              <p className="mt-0.5 text-[11px] text-muted-foreground">
                {submenu === "games" ? "Choisis ton jeu." : "Accède rapidement à nos services."}
              </p>
            </div>
            {submenu && (
              <button
                onClick={() => setSubmenu(null)}
                className="text-[11px] font-semibold text-muted-foreground hover:text-foreground"
              >
                Retour
              </button>
            )}
          </div>

          <AnimatePresence mode="wait">
            {submenu === "games" ? (
              <motion.div
                key="games"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                className="grid grid-cols-1 gap-2">
                {games.map(({ label, path }) => (
                  <button
                    key={label}
                    onClick={() => onNavigate(path)}
                    className="flex items-center gap-3 rounded-xl bg-surface px-3.5 py-3 text-left ring-1 ring-border transition hover:bg-border/40"
                  >
                    <span className="grid size-9 shrink-0 place-items-center rounded-lg" style={{ background: `${theme.chip}26`, color: theme.chip }}>
                      <Gamepad2 className="size-4" />
                    </span>
                    <span className="text-sm font-medium">{label}</span>
                    <ArrowRight className="ml-auto size-4 text-muted-foreground" />
                  </button>
                ))}
              </motion.div>
            ) : (
              <motion.div
                key="main"
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: 20 }}
                className="grid grid-cols-2 gap-2.5"
              >
                {items.map(({ id, label, icon: Icon, onClick }) => (
                  <button
                    key={id}
                    onClick={onClick}
                    className="flex items-center gap-3 rounded-xl bg-surface px-3.5 py-3 text-left ring-1 ring-border transition hover:bg-border/40"
                  >
                    <span className="grid size-9 shrink-0 place-items-center rounded-lg" style={{ background: `${theme.chip}26`, color: theme.chip }}>
                      <Icon className="size-4" />
                    </span>
                    <span className="text-sm font-medium">{label}</span>
                  </button>
                ))}
              </motion.div>
            )}
          </AnimatePresence>

          <a
            href={whatsappUrl}
            target="_blank"
            rel="noreferrer"
            onClick={() => onOpenChange(false)}
            className="mt-3 flex items-center justify-center gap-2 rounded-xl bg-foreground py-3 text-sm font-medium text-background"
          >
            <MessageCircle className="size-4" /> Nous contacter
          </a>
        </div>
      </DialogContent>
    </Dialog>
  );
}

type ThemeShape = { from: string; via: string; to: string; chip: string };

function QuickActionDrawer({
  action,
  onClose,
  platform,
  theme,
  whatsappUrl,
  onNavigate,
}: {
  action: QuickActionId | null;
  onClose: () => void;
  platform: string;
  theme: ThemeShape;
  whatsappUrl: string;
  onNavigate: (path: string) => void;
}) {
  const open = action !== null;
  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-h-[80vh] border-border bg-card text-foreground">
        <DialogTitle className="sr-only">Accès rapide</DialogTitle>
        <DialogDescription className="sr-only">Services complémentaires</DialogDescription>
        <div className="mx-auto w-full max-w-[40ch] px-5 pb-6 pt-3">
          {action === "refer" && (
            <>
              <h3 className="text-base font-semibold tracking-tight">Parraine & gagne</h3>
              <p className="mt-0.5 text-[11px] text-muted-foreground">250 FCFA offerts à chaque ami qui fait son 1er dépôt.</p>
              <div className="mt-3 flex items-center justify-between rounded-xl bg-surface p-3 ring-1 ring-border">
                <span className="font-mono text-sm">easy.bf/r/AIC2K9</span>
                <button onClick={() => copyToClipboard("https://easy.bf/r/AIC2K9")} className="text-xs font-semibold" style={{ color: theme.chip }}>
                  Copier
                </button>
              </div>
              <button
                onClick={() => {
                  onClose();
                  onNavigate("/parrainage");
                }}
                className="mt-3 w-full rounded-xl bg-foreground py-3 text-sm font-medium text-background"
              >
                Voir mon parrainage
              </button>
            </>
          )}
          {action === "more" && (
            <>
              <h3 className="text-base font-semibold tracking-tight">Autres services</h3>
              <p className="mt-0.5 text-[11px] text-muted-foreground">Services disponibles sur EASYSERVICE EXPRESS.</p>
              <div className="mt-3 grid grid-cols-2 gap-2">
                {[
                   { label: "Loterie", path: "/lottery" },
                   { label: "Pronostics", path: "/pronostics" },
                 ].map((s) => (
                  <button
                    key={s.label}
                    onClick={() => {
                      onClose();
                      onNavigate(s.path);
                    }}
                    className="rounded-xl bg-surface p-3 text-left text-xs ring-1 ring-border transition hover:bg-border/40"
                  >
                    <p className="font-medium">{s.label}</p>
                  </button>
                ))}
              </div>
              <a href={whatsappUrl} target="_blank" rel="noreferrer" className="mt-3 block rounded-xl bg-surface px-3 py-2.5 text-center text-[11px] font-medium ring-1 ring-border">
                Suggérer un service · WhatsApp
              </a>
            </>
          )}
          <div className="mt-4 text-center text-[10px] text-muted-foreground">Plateforme active : {platform}</div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default HomePage;
